/* Measures one copy of the rendered block list, clones it until the track is at least
   twice the viewport, and rewrites each card's `sizes` to its real width.
   See docs/theme-architecture/sections/footer-marquee.md. */

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
const MIN_COPIES = 2;
const MAX_COPIES = 12;
const RESIZE_DELAY = 200;

class FooterMarquee extends HTMLElement {
  connectedCallback() {
    this.motionQuery = window.matchMedia(REDUCED_MOTION);
    this.onMotionChange = () => this.build();
    this.motionQuery.addEventListener('change', this.onMotionChange);

    this.lastWidth = window.innerWidth;
    this.onResize = () => {
      if (window.innerWidth === this.lastWidth) return;
      this.lastWidth = window.innerWidth;
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.build(), RESIZE_DELAY);
    };
    window.addEventListener('resize', this.onResize, { passive: true });

    // The band lives in the footer of every page, so an always-running translated layer
    // would composite on pages nobody scrolls to the bottom of.
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => this.toggleAttribute('data-visible', entry.isIntersecting));
      },
      { rootMargin: '100px' }
    );
    this.intersectionObserver.observe(this);

    this.build();
  }

  disconnectedCallback() {
    this.motionQuery?.removeEventListener('change', this.onMotionChange);
    window.removeEventListener('resize', this.onResize);
    clearTimeout(this.resizeTimer);
    this.intersectionObserver?.disconnect();
  }

  build() {
    const track = this.querySelector('.footer-marquee__track');
    const source = track?.querySelector('.footer-marquee__copy:not([data-marquee-clone])');
    if (!track || !source) return;

    try {
      track.querySelectorAll('[data-marquee-clone]').forEach((el) => el.remove());
      this.style.removeProperty('--marquee-copies');
      this.style.removeProperty('--marquee-shift');

      // Before the mode branch: cards are sized by the base layer, so this is just as
      // true of a static row, and a reduced-motion visitor deserves the smaller image too.
      this.retagImageSizes(source);

      if (this.motionQuery.matches || source.children.length === 0) {
        this.setAttribute('data-mode', 'static');
        return;
      }

      // Measuring one copy needs the scrolling layout, which these two attributes switch on.
      this.removeAttribute('data-mode');
      this.setAttribute('data-ready', '');

      const copies = this.copiesNeeded(source);
      const fragment = document.createDocumentFragment();
      for (let i = 1; i < copies; i += 1) fragment.appendChild(this.duplicate(source));
      track.appendChild(fragment);

      this.style.setProperty('--marquee-copies', copies);
      this.style.setProperty('--marquee-shift', `${-100 / copies}%`);
    } finally {
      this.setAttribute('data-ready', '');
    }
  }

  // blocks/image.liquid ships sizes="100vw" against a width ladder topping out at 3840,
  // and takes no arguments that could tell it otherwise.
  retagImageSizes(source) {
    const card = source.firstElementChild;
    if (!card) return;
    const width = Math.ceil(card.getBoundingClientRect().width);
    if (!width) return;
    source
      .querySelectorAll('img, source')
      .forEach((el) => el.setAttribute('sizes', `${width}px`));
  }

  // One copy of five fixed-ratio cards is roughly one viewport, so a single clone and a
  // -50% wrap would expose bare background on anything wider.
  copiesNeeded(source) {
    const copyWidth = source.getBoundingClientRect().width;
    const bandWidth = this.getBoundingClientRect().width || window.innerWidth;
    if (!copyWidth) return MIN_COPIES;
    const needed = Math.ceil((bandWidth * 2) / copyWidth);
    return Math.min(MAX_COPIES, Math.max(MIN_COPIES, needed));
  }

  duplicate(source) {
    const clone = source.cloneNode(true);
    clone.setAttribute('data-marquee-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    // Strip what has to stay unique to the real copy, and take the clone out of the tab
    // order — focusable content inside aria-hidden is its own a11y failure.
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
    clone
      .querySelectorAll('[data-shopify-editor-block]')
      .forEach((el) => el.removeAttribute('data-shopify-editor-block'));
    clone
      .querySelectorAll('a, button, input, select, textarea, [tabindex]')
      .forEach((el) => el.setAttribute('tabindex', '-1'));
    return clone;
  }
}

if (!customElements.get('footer-marquee')) {
  customElements.define('footer-marquee', FooterMarquee);
}
